import { cn } from '@/lib/utils';

// ============================================
// PRINT-QUALITY TEMPLATE BACKGROUNDS
// Each template has a visually rich, designed backdrop
// that feels complete BEFORE text is added
// ============================================

// SVG patterns as data URIs for textures
const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`;

const FINE_LINEN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`;

// Botanical leaf SVG illustration (hand-drawn style)
const LeafIllustration = ({ className, flip }: { className?: string; flip?: boolean }) => (
  <svg 
    viewBox="0 0 100 150" 
    className={cn('absolute pointer-events-none', className)}
    style={{ transform: flip ? 'scaleX(-1)' : undefined }}
  >
    <defs>
      <linearGradient id="leafGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="rgb(134, 197, 137)" stopOpacity="0.6" />
        <stop offset="100%" stopColor="rgb(74, 137, 77)" stopOpacity="0.4" />
      </linearGradient>
    </defs>
    {/* Main leaf shape */}
    <path 
      d="M50 10 Q70 30 65 60 Q60 90 50 120 Q40 90 35 60 Q30 30 50 10" 
      fill="url(#leafGrad)" 
      stroke="rgb(74, 137, 77)" 
      strokeWidth="0.5"
      strokeOpacity="0.3"
    />
    {/* Leaf vein */}
    <path 
      d="M50 15 L50 115" 
      stroke="rgb(74, 137, 77)" 
      strokeWidth="0.5" 
      strokeOpacity="0.3"
      fill="none"
    />
    {/* Side veins */}
    <path 
      d="M50 30 Q60 35 62 45 M50 50 Q60 55 61 65 M50 70 Q58 75 59 85" 
      stroke="rgb(74, 137, 77)" 
      strokeWidth="0.3" 
      strokeOpacity="0.2"
      fill="none"
    />
  </svg>
);

// Floral/botanical corner illustration (watercolor style)
const FloralCorner = ({ className, position }: { className?: string; position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' }) => {
  const transforms: Record<string, string> = {
    'top-left': '',
    'top-right': 'scaleX(-1)',
    'bottom-left': 'scaleY(-1)',
    'bottom-right': 'scale(-1)',
  };
  
  return (
    <svg 
      viewBox="0 0 200 200" 
      className={cn('absolute pointer-events-none', className)}
      style={{ transform: transforms[position] }}
    >
      <defs>
        <radialGradient id="flowerCenter" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgb(251, 191, 36)" stopOpacity="0.8" />
          <stop offset="100%" stopColor="rgb(245, 158, 11)" stopOpacity="0.4" />
        </radialGradient>
        <linearGradient id="petalGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgb(252, 231, 243)" stopOpacity="0.7" />
          <stop offset="100%" stopColor="rgb(244, 114, 182)" stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="leafGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgb(134, 197, 137)" stopOpacity="0.6" />
          <stop offset="100%" stopColor="rgb(74, 137, 77)" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      
      {/* Decorative leaves */}
      <ellipse cx="40" cy="60" rx="25" ry="40" fill="url(#leafGrad2)" transform="rotate(-30 40 60)" />
      <ellipse cx="80" cy="40" rx="20" ry="35" fill="url(#leafGrad2)" transform="rotate(-60 80 40)" />
      <ellipse cx="60" cy="100" rx="18" ry="30" fill="url(#leafGrad2)" transform="rotate(20 60 100)" />
      
      {/* Main flower */}
      <ellipse cx="120" cy="80" rx="15" ry="25" fill="url(#petalGrad)" transform="rotate(-45 120 80)" />
      <ellipse cx="140" cy="60" rx="15" ry="25" fill="url(#petalGrad)" transform="rotate(-10 140 60)" />
      <ellipse cx="150" cy="90" rx="15" ry="25" fill="url(#petalGrad)" transform="rotate(30 150 90)" />
      <ellipse cx="130" cy="100" rx="15" ry="25" fill="url(#petalGrad)" transform="rotate(70 130 100)" />
      <circle cx="135" cy="80" r="8" fill="url(#flowerCenter)" />
      
      {/* Small accent flowers */}
      <circle cx="60" cy="140" r="6" fill="url(#petalGrad)" opacity="0.6" />
      <circle cx="100" cy="150" r="4" fill="url(#flowerCenter)" opacity="0.5" />
      
      {/* Stems */}
      <path d="M40 60 Q50 80 60 100 Q70 120 60 140" stroke="rgb(74, 137, 77)" strokeWidth="1" strokeOpacity="0.3" fill="none" />
      <path d="M120 80 Q100 100 80 120" stroke="rgb(74, 137, 77)" strokeWidth="0.5" strokeOpacity="0.2" fill="none" />
    </svg>
  );
};

// Decorative flourish for dividers
const OrnamentalDivider = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 200 30" className={cn('w-48 h-6', className)}>
    <defs>
      <linearGradient id="dividerGrad" x1="0%" y1="50%" x2="100%" y2="50%">
        <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
        <stop offset="30%" stopColor="currentColor" stopOpacity="0.4" />
        <stop offset="50%" stopColor="currentColor" stopOpacity="0.6" />
        <stop offset="70%" stopColor="currentColor" stopOpacity="0.4" />
        <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
      </linearGradient>
    </defs>
    <line x1="0" y1="15" x2="85" y2="15" stroke="url(#dividerGrad)" strokeWidth="1" />
    <circle cx="100" cy="15" r="3" fill="currentColor" opacity="0.4" />
    <line x1="115" y1="15" x2="200" y2="15" stroke="url(#dividerGrad)" strokeWidth="1" />
    {/* Decorative curls */}
    <path d="M80 15 Q85 10 90 15" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
    <path d="M110 15 Q115 20 120 15" stroke="currentColor" strokeWidth="0.5" strokeOpacity="0.3" fill="none" />
  </svg>
);

// Timeline/journey accent marks
const TimelineAccent = ({ className }: { className?: string }) => (
  <div className={cn('flex flex-col items-center gap-2', className)}>
    <div className="w-3 h-3 rounded-full bg-primary/40 border-2 border-primary/60" />
    <div className="w-px h-8 bg-gradient-to-b from-primary/40 to-primary/10" />
  </div>
);

// ============================================
// TEMPLATE BACKGROUND CONFIGURATIONS
// ============================================

export interface TemplateBackgroundConfig {
  id: string;
  name: string;
  emotionalIntent: 'keepsake' | 'reflection' | 'storytelling' | 'gratitude' | 'journey' | 'moment';
  background: React.ReactNode;
  overlays: React.ReactNode;
  textColors: {
    title: string;
    body: string;
    accent: string;
    muted: string;
  };
  typography: {
    titleFont: string;
    bodyFont: string;
  };
}

// 1. FOREVER IN MY HEART - Hero template (benchmark quality)
export const ForeverInMyHeartBackground = () => (
  <>
    {/* Warm cream/parchment base */}
    <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-orange-50/80 to-yellow-50 dark:from-amber-950 dark:via-orange-950/80 dark:to-yellow-950" />
    
    {/* Paper texture overlay */}
    <div className="absolute inset-0 opacity-[0.12] dark:opacity-[0.06]" style={{ backgroundImage: NOISE_TEXTURE }} />
    
    {/* Linen weave texture */}
    <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]" style={{
      backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(139, 69, 19, 0.08) 3px, rgba(139, 69, 19, 0.08) 4px), repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(139, 69, 19, 0.08) 3px, rgba(139, 69, 19, 0.08) 4px)',
    }} />
    
    {/* Warm vignette for depth */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(180,120,60,0.06)_60%,rgba(120,80,40,0.12)_100%)] dark:bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(80,50,20,0.2)_60%,rgba(40,25,10,0.4)_100%)]" />
    
    {/* Subtle aged edges */}
    <div className="absolute inset-0 bg-gradient-to-t from-amber-100/30 via-transparent to-amber-50/20 dark:from-amber-900/20 dark:to-amber-900/10" />
  </>
);

export const ForeverInMyHeartOverlays = () => (
  <>
    {/* Botanical/floral border illustrations */}
    <FloralCorner position="top-left" className="w-32 h-32 md:w-48 md:h-48 top-0 left-0 opacity-60" />
    <FloralCorner position="top-right" className="w-32 h-32 md:w-48 md:h-48 top-0 right-0 opacity-60" />
    <FloralCorner position="bottom-left" className="w-24 h-24 md:w-36 md:h-36 bottom-0 left-0 opacity-50" />
    <FloralCorner position="bottom-right" className="w-24 h-24 md:w-36 md:h-36 bottom-0 right-0 opacity-50" />
    
    {/* Corner accent leaves */}
    <LeafIllustration className="w-12 h-16 top-16 left-4 opacity-40 md:w-16 md:h-24" />
    <LeafIllustration className="w-12 h-16 top-16 right-4 opacity-40 md:w-16 md:h-24" flip />
  </>
);

// 2. YOUR STORY MATTERS - Reflection/affirmation
export const YourStoryMattersBackground = () => (
  <>
    {/* Soft sky/cloud gradient */}
    <div className="absolute inset-0 bg-gradient-to-b from-sky-100 via-blue-50/60 to-rose-50/40 dark:from-sky-950 dark:via-blue-950/60 dark:to-rose-950/40" />
    
    {/* Atmospheric haze */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_150%_100%_at_50%_0%,rgba(186,230,253,0.4),transparent_60%)] dark:bg-[radial-gradient(ellipse_150%_100%_at_50%_0%,rgba(30,58,138,0.3),transparent_60%)]" />
    
    {/* Soft cloud-like formations */}
    <div className="absolute top-10 left-10 w-64 h-32 bg-white/30 dark:bg-white/5 rounded-full blur-3xl" />
    <div className="absolute top-20 right-20 w-48 h-24 bg-white/25 dark:bg-white/5 rounded-full blur-2xl" />
    <div className="absolute bottom-20 left-1/3 w-56 h-28 bg-rose-100/20 dark:bg-rose-900/10 rounded-full blur-2xl" />
    
    {/* Gentle texture */}
    <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.02]" style={{ backgroundImage: NOISE_TEXTURE }} />
    
    {/* Warm light from bottom */}
    <div className="absolute inset-0 bg-gradient-to-t from-amber-50/40 via-transparent to-transparent dark:from-amber-950/20" />
  </>
);

export const YourStoryMattersOverlays = () => (
  <>
    {/* Soft glowing orbs for inspiration */}
    <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-amber-200/40 dark:bg-amber-400/20 rounded-full blur-sm" />
    <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-rose-200/30 dark:bg-rose-400/15 rounded-full blur-sm" />
    <div className="absolute bottom-1/4 right-1/4 w-5 h-5 bg-sky-200/30 dark:bg-sky-400/15 rounded-full blur-md" />
  </>
);

// 3. ONCE UPON A TIME - Storytelling/memory
export const OnceUponATimeBackground = () => (
  <>
    {/* Vintage parchment base */}
    <div className="absolute inset-0 bg-gradient-to-br from-amber-100 via-yellow-50/90 to-orange-100/80 dark:from-amber-950 dark:via-yellow-950/90 dark:to-orange-950/80" />
    
    {/* Aged paper texture */}
    <div className="absolute inset-0 opacity-[0.15] dark:opacity-[0.08]" style={{ backgroundImage: NOISE_TEXTURE }} />
    
    {/* Storybook page border effect */}
    <div className="absolute inset-4 md:inset-8 border border-amber-200/50 dark:border-amber-700/30 rounded-sm" />
    <div className="absolute inset-6 md:inset-10 border border-amber-200/30 dark:border-amber-700/20 rounded-sm" />
    
    {/* Aged corner shadows */}
    <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-amber-200/30 to-transparent dark:from-amber-900/20" />
    <div className="absolute bottom-0 right-0 w-32 h-32 bg-gradient-to-tl from-amber-200/30 to-transparent dark:from-amber-900/20" />
    
    {/* Subtle sepia wash */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-100/20 to-amber-200/30 dark:from-transparent dark:via-amber-950/10 dark:to-amber-900/20" />
  </>
);

export const OnceUponATimeOverlays = () => (
  <>
    {/* Decorative corner flourishes */}
    <svg className="absolute top-4 left-4 md:top-8 md:left-8 w-16 h-16 md:w-24 md:h-24 text-amber-700/30 dark:text-amber-400/20" viewBox="0 0 100 100">
      <path d="M10 50 Q10 10 50 10 M50 10 Q30 30 30 50 M30 50 Q30 30 50 30" fill="none" stroke="currentColor" strokeWidth="1" />
    </svg>
    <svg className="absolute top-4 right-4 md:top-8 md:right-8 w-16 h-16 md:w-24 md:h-24 text-amber-700/30 dark:text-amber-400/20" viewBox="0 0 100 100" style={{ transform: 'scaleX(-1)' }}>
      <path d="M10 50 Q10 10 50 10 M50 10 Q30 30 30 50 M30 50 Q30 30 50 30" fill="none" stroke="currentColor" strokeWidth="1" />
    </svg>
    
    {/* Subtle leaf accents */}
    <LeafIllustration className="w-10 h-14 bottom-10 left-6 opacity-30" />
  </>
);

// 4. THE MOMENT - One powerful memory
export const TheMomentBackground = () => (
  <>
    {/* Ethereal gradient (sunrise/dusk feel) */}
    <div className="absolute inset-0 bg-gradient-to-br from-rose-100/90 via-purple-50/70 to-amber-50/80 dark:from-rose-950/50 dark:via-purple-950/40 dark:to-amber-950/30" />
    
    {/* Soft light rays */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(251,207,232,0.4),transparent_50%)] dark:bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(131,24,67,0.2),transparent_50%)]" />
    
    {/* Warm glow from bottom */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_100%_60%_at_50%_120%,rgba(251,146,60,0.25),transparent_50%)] dark:bg-[radial-gradient(ellipse_100%_60%_at_50%_120%,rgba(251,146,60,0.1),transparent_50%)]" />
    
    {/* Central ethereal glow */}
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.4),transparent_60%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.08),transparent_60%)]" />
    
    {/* Very subtle texture */}
    <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]" style={{ backgroundImage: FINE_LINEN }} />
  </>
);

export const TheMomentOverlays = () => (
  <>
    {/* Floating light particles */}
    <div className="absolute top-1/4 left-1/5 w-2 h-2 bg-white/50 dark:bg-white/20 rounded-full blur-sm animate-pulse" style={{ animationDuration: '3s' }} />
    <div className="absolute top-1/3 right-1/4 w-1.5 h-1.5 bg-rose-200/60 dark:bg-rose-400/20 rounded-full blur-sm animate-pulse" style={{ animationDuration: '4s', animationDelay: '1s' }} />
    <div className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-amber-100/50 dark:bg-amber-400/15 rounded-full blur-sm animate-pulse" style={{ animationDuration: '5s', animationDelay: '2s' }} />
  </>
);

// 5. REFLECTIONS ON A YEAR - Annual reflection
export const ReflectionsOnAYearBackground = () => (
  <>
    {/* Editorial/magazine neutral base */}
    <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-stone-50 to-zinc-100 dark:from-slate-900 dark:via-stone-900 dark:to-zinc-900" />
    
    {/* Subtle paper grain */}
    <div className="absolute inset-0 opacity-[0.06] dark:opacity-[0.03]" style={{ backgroundImage: NOISE_TEXTURE }} />
    
    {/* Section divider lines (timeline feel) */}
    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-slate-300/50 to-transparent dark:via-slate-600/30" />
    
    {/* Horizontal rhythm lines */}
    <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.02]" style={{
      backgroundImage: 'repeating-linear-gradient(180deg, transparent, transparent 80px, rgba(100,116,139,0.3) 80px, rgba(100,116,139,0.3) 81px)',
    }} />
    
    {/* Warm accent glow */}
    <div className="absolute top-0 left-0 right-0 h-1/4 bg-gradient-to-b from-amber-50/40 to-transparent dark:from-amber-950/20" />
  </>
);

export const ReflectionsOnAYearOverlays = () => (
  <>
    {/* Timeline accent markers */}
    <div className="absolute left-8 top-1/4 flex flex-col gap-16 opacity-40">
      <div className="w-2 h-2 rounded-full bg-primary/60" />
      <div className="w-2 h-2 rounded-full bg-primary/40" />
      <div className="w-2 h-2 rounded-full bg-primary/30" />
    </div>
  </>
);

// 6. THANK YOU FOR BEING YOU - Gratitude
export const ThankYouBackground = () => (
  <>
    {/* Soft neutral warmth */}
    <div className="absolute inset-0 bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-rose-50/50 dark:from-orange-950/40 dark:via-amber-950/30 dark:to-rose-950/25" />
    
    {/* Card-like warmth layer */}
    <div className="absolute inset-4 md:inset-8 bg-white/50 dark:bg-slate-800/30 rounded-2xl shadow-inner" />
    
    {/* Subtle linen texture */}
    <div className="absolute inset-0 opacity-[0.05] dark:opacity-[0.02]" style={{ backgroundImage: FINE_LINEN }} />
    
    {/* Warm glow from center */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.08),transparent_60%)] dark:bg-[radial-gradient(ellipse_at_center,rgba(251,191,36,0.04),transparent_60%)]" />
  </>
);

export const ThankYouOverlays = () => (
  <>
    {/* Decorative dots pattern */}
    <div className="absolute top-4 right-4 flex gap-1.5 opacity-40">
      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      <div className="w-1.5 h-1.5 rounded-full bg-rose-300" />
      <div className="w-1.5 h-1.5 rounded-full bg-amber-300" />
    </div>
    <div className="absolute bottom-4 left-4 flex gap-1.5 opacity-40">
      <div className="w-1.5 h-1.5 rounded-full bg-amber-300" />
      <div className="w-1.5 h-1.5 rounded-full bg-rose-300" />
      <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
    </div>
    
    {/* Soft heart accent */}
    <svg className="absolute top-1/2 right-4 w-8 h-8 text-rose-300/30 dark:text-rose-400/15" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  </>
);

// 7. GROWTH IN EVERY STEP - Journey/progress
export const GrowthBackground = () => (
  <>
    {/* Warm cream with growth feel */}
    <div className="absolute inset-0 bg-gradient-to-b from-amber-50 via-green-50/40 to-emerald-50/30 dark:from-amber-950 dark:via-green-950/30 dark:to-emerald-950/20" />
    
    {/* Vertical flow gradient */}
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-primary/10 dark:from-transparent dark:via-primary/3 dark:to-primary/5" />
    
    {/* Subtle path/timeline visual */}
    <div className="absolute left-1/4 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/20 to-primary/40 dark:via-primary/10 dark:to-primary/20" />
    
    {/* Light texture */}
    <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.02]" style={{ backgroundImage: NOISE_TEXTURE }} />
    
    {/* Upward glow (growth symbolism) */}
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(104,177,108,0.15),transparent_60%)] dark:bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(104,177,108,0.08),transparent_60%)]" />
  </>
);

export const GrowthOverlays = () => (
  <>
    {/* Growth timeline markers */}
    <div className="absolute left-1/4 top-1/4 -translate-x-1/2">
      <div className="w-3 h-3 rounded-full bg-primary/30 border-2 border-primary/50" />
    </div>
    <div className="absolute left-1/4 top-1/2 -translate-x-1/2">
      <div className="w-4 h-4 rounded-full bg-primary/40 border-2 border-primary/60" />
    </div>
    <div className="absolute left-1/4 top-3/4 -translate-x-1/2">
      <div className="w-5 h-5 rounded-full bg-primary/50 border-2 border-primary/70" />
    </div>
    
    {/* Small leaf accents */}
    <LeafIllustration className="w-8 h-12 top-1/4 left-1/4 ml-4 opacity-40" />
    <LeafIllustration className="w-10 h-14 top-1/2 left-1/4 ml-6 opacity-50" />
  </>
);

// Export OrnamentalDivider for use in templates
export { OrnamentalDivider, LeafIllustration, FloralCorner, TimelineAccent };
